import { useState } from 'react';
import { Trophy, Medal, Star, ChevronDown } from 'lucide-react';
import { TeamMember } from '../../lib/types';

interface TeamMemberCardProps {
  member: TeamMember;
}

export function TeamMemberCard({ member }: TeamMemberCardProps) {
  const [showAllAchievements, setShowAllAchievements] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const displayedAchievements = showAllAchievements ? member.achievements : member.achievements.slice(0, 2);
  
  // Create a truncated description (3 lines, approximately 180 characters)
  const shortDescription = member.description.length > 180 && !showFullDescription
    ? `${member.description.slice(0, 180)}...`
    : member.description;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300">
      <div className="relative">
        <img
          src={member.imageUrl}
          alt={member.name}
          className="w-full h-80 object-cover"
        />
      </div>
      <div className="p-6">
        <h2 className="text-3xl font-bold mb-2">{member.name}</h2>
        <h3 className="text-xl text-orange-500 font-semibold mb-4">{member.title}</h3>
        
        <div className="mb-6">
          <p className="text-gray-700">{shortDescription}</p>
          {member.description.length > 180 && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className="text-orange-500 hover:text-orange-600 text-sm mt-2 flex items-center gap-1"
            >
              {showFullDescription ? 'Vezi mai puțin' : 'Vezi mai mult'}
              <ChevronDown className={`w-4 h-4 transition-transform ${showFullDescription ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
        
        <div className="mb-6">
          <h4 className="font-semibold mb-2">Specializări:</h4>
          <div className="flex flex-wrap gap-2">
            {member.specialties.map((specialty, index) => (
              <span
                key={index}
                className="bg-gray-100 px-3 py-1 rounded-full text-sm"
              >
                {specialty}
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          {displayedAchievements.map((achievement, index) => (
            <div key={index} className="flex items-center gap-2">
              {index === 0 && <Trophy className="text-orange-500" size={20} />}
              {index === 1 && <Medal className="text-orange-500" size={20} />}
              {index > 1 && <Star className="text-orange-500" size={20} />}
              <span>{achievement}</span>
            </div>
          ))}
          
          {member.achievements.length > 2 && (
            <button
              onClick={() => setShowAllAchievements(!showAllAchievements)}
              className="text-orange-500 hover:text-orange-600 text-sm mt-2 flex items-center gap-1"
            >
              {showAllAchievements ? 'Vezi mai puțin' : 'Vezi toate realizările'}
              <ChevronDown className={`w-4 h-4 transition-transform ${showAllAchievements ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}